// public/js/features/files/explorer.css.js
// Styles for the whole files feature: the #files explorer, the asset detail
// file/photo sections and the viewer modal.
//
// WHY a JS module and not app.css: app.css is the shared shell stylesheet that
// every page pays for. These rules only matter once a user actually opens a
// file surface, and keeping them next to the markup that uses them means the
// feature stays self-contained in features/files/ — delete the folder and no
// orphan CSS is left behind. `injectFilesCss()` is idempotent, so every entry
// point can call it without coordinating.
//
// All selectors are prefixed `fx-` so they can never collide with Bootstrap or
// with the shell classes in app.css.

export const FILES_CSS = `
/* ── Toolbar / filter strip ─────────────────────────────────────────────── */
.fx-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: .5rem; }
.fx-filters { display: grid; gap: .5rem; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); }
.fx-viewtoggle .btn { --bs-btn-padding-y: .2rem; --bs-btn-padding-x: .55rem; }

/* ── Thumbnails ─────────────────────────────────────────────────────────── */
/* One box for every preview so a missing/failed thumbnail never reflows the
   grid — the type icon simply fills the same square. */
.fx-thumb {
  position: relative; flex-shrink: 0; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  background: #f1f5f9; border: 1px solid var(--line, #e5e7eb); border-radius: 8px;
  color: #94a3b8;
}
.fx-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.fx-thumb-sm { width: 44px; height: 44px; font-size: 1rem; }
.fx-thumb-lg { width: 100%; aspect-ratio: 4 / 3; border-radius: 8px 8px 0 0; border-width: 0 0 1px 0; font-size: 1.9rem; }

/* ── Tile grid ──────────────────────────────────────────────────────────── */
.fx-tiles { display: grid; gap: .75rem; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); }
.fx-tile {
  display: flex; flex-direction: column; overflow: hidden; cursor: pointer;
  background: #fff; border: 1px solid var(--line, #e5e7eb); border-radius: 10px;
  transition: box-shadow .15s ease, border-color .15s ease, transform .15s ease;
}
.fx-tile:hover { border-color: #cbd5e1; box-shadow: 0 4px 12px rgba(15, 23, 42, .08); transform: translateY(-1px); }
.fx-tile-body { padding: .5rem .6rem .6rem; display: flex; flex-direction: column; gap: .25rem; min-width: 0; }
.fx-tile-title {
  font-size: .8rem; font-weight: 600; color: var(--ink, #0f172a); line-height: 1.25;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.fx-tile-meta { font-size: .68rem; color: #94a3b8; display: flex; flex-wrap: wrap; gap: .35rem; }
.fx-tile-actions { display: flex; gap: .2rem; margin-top: .15rem; }

/* ── List rows ──────────────────────────────────────────────────────────── */
.fx-row { display: flex; align-items: center; gap: .65rem; padding: .5rem .65rem; cursor: pointer; }
.fx-row + .fx-row { border-top: 1px solid var(--line, #e5e7eb); }
.fx-row:hover { background: #f8fafc; }
.fx-row-main { min-width: 0; flex: 1 1 auto; }
.fx-row-title { font-size: .84rem; font-weight: 600; color: var(--ink, #0f172a); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fx-row-sub { font-size: .72rem; color: #94a3b8; display: flex; flex-wrap: wrap; gap: .5rem; }
.fx-row-actions { display: flex; gap: .2rem; flex-shrink: 0; }

/* Doc-type group heading inside the asset Files section. */
.fx-group-head {
  display: flex; align-items: center; gap: .4rem;
  font-size: .72rem; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
  color: #64748b; padding: .45rem .65rem; background: #f8fafc;
  border-bottom: 1px solid var(--line, #e5e7eb);
}

/* ── Photo gallery ──────────────────────────────────────────────────────── */
.fx-photos { display: grid; gap: .6rem; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); }
.fx-photo {
  position: relative; overflow: hidden; cursor: pointer; aspect-ratio: 1 / 1;
  border: 1px solid var(--line, #e5e7eb); border-radius: 10px; background: #f1f5f9;
}
.fx-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
.fx-photo.is-primary { border-color: var(--brand-primary, #2563eb); box-shadow: 0 0 0 2px rgba(37, 99, 235, .18); }
.fx-photo-badge {
  position: absolute; top: 6px; left: 6px; z-index: 2;
  font-size: .64rem; font-weight: 700; padding: .2em .55em; border-radius: 999px;
  background: var(--brand-primary, #2563eb); color: #fff;
}
/* Actions stay visible on touch devices (no hover) — see the media query below. */
.fx-photo-actions {
  position: absolute; inset: auto 0 0 0; z-index: 2;
  display: flex; justify-content: center; gap: .25rem; padding: .35rem;
  background: linear-gradient(to top, rgba(15, 23, 42, .75), rgba(15, 23, 42, 0));
  opacity: 0; transition: opacity .15s ease;
}
.fx-photo:hover .fx-photo-actions, .fx-photo:focus-within .fx-photo-actions { opacity: 1; }
@media (hover: none) { .fx-photo-actions { opacity: 1; } }
.fx-photo-actions .btn { --bs-btn-padding-y: .1rem; --bs-btn-padding-x: .4rem; --bs-btn-font-size: .7rem; }

/* ── Ask-AI answer card ─────────────────────────────────────────────────── */
.fx-answer {
  border: 1px solid #bfdbfe; border-left: 4px solid var(--brand-primary, #2563eb);
  background: #f5f9ff; border-radius: 10px; padding: .85rem 1rem;
}
.fx-answer-text { white-space: pre-wrap; font-size: .88rem; color: var(--ink, #0f172a); line-height: 1.5; }
.fx-answer-sources { display: flex; flex-wrap: wrap; gap: .35rem; margin-top: .6rem; }

/* ── Misc ───────────────────────────────────────────────────────────────── */
.fx-tag { font-size: .68rem; font-weight: 600; padding: .18em .55em; border-radius: 999px; background: #eef2ff; color: #4338ca; }
.fx-empty { text-align: center; color: #94a3b8; padding: 2.25rem 1rem; }
.fx-empty i { font-size: 1.75rem; display: block; margin-bottom: .6rem; opacity: .55; }
.fx-empty-sm { text-align: center; color: #94a3b8; font-size: .82rem; padding: 1.1rem .5rem; }

/* ── Viewer modal ───────────────────────────────────────────────────────── */
.fx-viewer-stage {
  display: flex; align-items: center; justify-content: center;
  background: #0f172a; border-radius: 8px; min-height: 220px; overflow: hidden;
}
.fx-viewer-img { max-width: 100%; max-height: 68vh; object-fit: contain; cursor: zoom-in; display: block; }
.fx-viewer-embed { width: 100%; height: 70vh; border: 0; background: #0f172a; border-radius: 8px; }
.fx-meta { display: grid; grid-template-columns: max-content 1fr; gap: .3rem .85rem; font-size: .82rem; }
.fx-meta dt { color: #64748b; font-weight: 600; }
.fx-meta dd { margin: 0; color: var(--ink, #0f172a); overflow-wrap: anywhere; }
`;

const STYLE_ID = 'fx-files-css';

/** Inject the feature stylesheet once per document. Safe to call repeatedly. */
export function injectFilesCss() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = FILES_CSS;
  document.head.appendChild(el);
}

export default FILES_CSS;
